import { useEffect, useState } from 'react';
import type { TenantConfig } from '@/types';

interface BrandMarkProps {
  tenant: TenantConfig;
  size?: 'sm' | 'lg';
  /**
   * Reports whether the mark still needs the tenant NAME rendered as text
   * beside it: true for the monogram fallback (no logo, or a logo that failed
   * to load) and for a SYMBOL-shaped logo (a square icon says nothing without
   * the name); false for a wordmark-shaped logo, which IS the name.
   */
  onNeedsName?: (needsName: boolean) => void;
}

/**
 * Reserved logo band heights. The box is laid out at its full height BEFORE
 * the image loads, so a logo landing over a slow network never shifts the
 * content (or the CTA) below it.
 */
const LOGO_BAND_PX = { sm: 34, lg: 64 } as const;

/**
 * Aspect ratio (w/h) at and above which a logo reads as a WORDMARK (carries
 * its own name); below it, a symbol-shaped mark (e.g. a 350×350 icon) keeps
 * the tenant name as text beside it.
 */
const WORDMARK_MIN_ASPECT = 2;

/**
 * The white-label logo slot (owner round 2026-08-28 item 4).
 *
 * When the tenant has a real logo (branding.logoUrl — served config first,
 * https-sanitised upstream), the logo IS the brand mark: a free-standing
 * image inside a RESERVED band (zero layout shift on load), contained and
 * never cropped into the monogram square or stretched, down to 320px phones.
 * Alt text is the tenant's name, so the mark still names the tenant to AT.
 *
 * Failure honesty: a logo that errors falls back to the monogram — a broken-
 * image glyph must never be the primary brand mark — and the fallback state
 * is keyed by URL, so a CHANGED served logo gets a fresh chance.
 *
 * The `--brand`-filled monogram square is the NO-LOGO (or broken-logo)
 * fallback only.
 */
export function BrandMark({ tenant, size = 'sm', onNeedsName }: BrandMarkProps): JSX.Element {
  const { logoUrl, initial } = tenant.branding;
  // Keyed by URL: an error verdict (or a symbol verdict) for one image must
  // never apply to a different logo the served config swaps in later.
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const [symbolUrl, setSymbolUrl] = useState<string | null>(null);
  const broken = logoUrl != null && brokenUrl === logoUrl;
  const showLogo = logoUrl != null && !broken;
  const isSymbol = showLogo && symbolUrl === logoUrl;

  const needsName = !showLogo || isSymbol;
  useEffect(() => {
    onNeedsName?.(needsName);
  }, [needsName, onNeedsName]);

  if (showLogo) {
    return (
      <span
        className={size === 'lg' ? 'brand-logo-box lg' : 'brand-logo-box'}
        style={{ height: LOGO_BAND_PX[size] }}
      >
        <img
          className={size === 'lg' ? 'brand-logo lg' : 'brand-logo'}
          src={logoUrl}
          alt={tenant.name}
          onError={() => setBrokenUrl(logoUrl)}
          onLoad={(e) => {
            const img = e.currentTarget;
            const symbol =
              img.naturalHeight > 0 &&
              img.naturalWidth / img.naturalHeight < WORDMARK_MIN_ASPECT;
            setSymbolUrl(symbol ? logoUrl : null);
          }}
        />
      </span>
    );
  }
  const cls = size === 'lg' ? 'brand-mark lg' : 'brand-mark';
  return (
    <div className={cls} aria-hidden={true}>
      {(initial ?? tenant.name.charAt(0)).toUpperCase()}
    </div>
  );
}
